import { useIsAuthenticated } from "@azure/msal-react";
import { useState } from "react";
import {
  Box, Typography, Button, Menu, MenuItem, Divider, useTheme,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckIcon from "@mui/icons-material/Check";
import PersonIcon from "@mui/icons-material/Person";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useImpersonation } from "../contexts/ImpersonationContext";
import { FEATURE_VIEW_AS_USER } from "../utils/env";
import { listOrgUsers } from "../utils/api";

/**
 * Sidebar dropdown for admins to choose which org's data they want to act on, and
 * (behind FEATURE_VIEW_AS_USER) which specific USER within that org to view as.
 *
 * Layout is an inline accordion: click an org row and its users expand right
 * beneath it. Expanding only browses (fetches that org's users) — nothing is
 * committed until you pick "All users (org-wide)" or a specific user, at which
 * point we set the acting org/user and reload. The user is always resolved
 * WITHIN an org because analytics data is org-scoped (X-Acting-User must belong
 * to X-Acting-Org).
 *
 * Hidden entirely for non-admins.
 */
export default function OrgSwitcher({ collapsed }) {
  const isAuth = useIsAuthenticated();
  const theme = useTheme();
  const {
    adminStatus, adminProbeFailed, orgs, actingOrgId, actingOrg, setActingOrg, clearActing,
    actingUserId, actingUser, setActingUser, retryAdminProbe,
  } = useImpersonation();
  const [anchorEl, setAnchorEl] = useState(null);
  // Which org row is expanded in the menu, plus that org's users (browse-only,
  // pre-commit). Independent of the committed acting org.
  const [expandedOrgId, setExpandedOrgId] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Only an authoritative "you are not an admin" hides this. While the probe is still in
  // flight (or has failed) we keep the control on screen — disabled, or offering a retry —
  // because silently disappearing is the bug: it read as "the feature is gone" and the only
  // known workaround was reloading until the probe happened to win.
  // Staff-only control: shown to a signed-in admin (the per-browser hint or the probe said so), and to
  // nobody else. It used to hide only on an authoritative "not an admin", so a signed-out visitor —
  // never probed — and anyone on a fresh browser saw a "Checking access…" button that never resolved.
  // A known admin still keeps it through a failed probe (the retry label), which was the point.
  if (!isAuth || adminStatus !== "yes") return null;
  const pending = false;

  const label = adminProbeFailed
    ? "Acting as: unavailable — retry"
    : pending
      ? "Checking access…"
      : actingUser
        ? (actingUser.email || actingUser.display_name || "user")
        // Fall back to the raw id: the acting header IS being sent, so the label must never
        // read "self" just because the org list didn't load.
        : actingOrgId ? (actingOrg?.domain || actingOrgId) : "Acting as: self";

  const userKey = (u) => u.user_id || u.uid || u.email;

  const loadUsersFor = async (orgId) => {
    setLoadingUsers(true);
    setExpandedUsers([]);
    try {
      const data = await listOrgUsers(orgId);
      const arr = Array.isArray(data) ? data : (data.users || []);
      // View-as targets must be real members of THIS org. The endpoint also
      // injects global admins (for the sharee picker) tagged is_member:false —
      // drop those (they'd be mislabeled read-only and silently no-op if picked
      // since their org != the acting org). `!== false` keeps rows from an older
      // backend that predates the flag.
      setExpandedUsers(arr.filter((u) => u.is_member !== false));
    } catch (_) {
      setExpandedUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpen = (e) => {
    // Probe failed → the button becomes a retry affordance instead of dead UI.
    if (adminProbeFailed) { retryAdminProbe(); return; }
    if (pending) return;
    setAnchorEl(e.currentTarget);
    // Re-open on the currently-acting org so the current selection is visible.
    if (FEATURE_VIEW_AS_USER && actingOrgId) {
      setExpandedOrgId(actingOrgId);
      loadUsersFor(actingOrgId);
    }
  };
  const handleClose = () => setAnchorEl(null);

  const toggleExpand = (orgId) => {
    if (expandedOrgId === orgId) { setExpandedOrgId(null); return; }
    setExpandedOrgId(orgId);
    loadUsersFor(orgId);
  };

  // ── Commit actions (each reloads so all data-dependent views re-fetch) ──
  const actAsSelf = () => {
    clearActing();
    handleClose();
    window.location.reload();
  };
  const commitOrgWide = (orgId) => {
    setActingOrg(orgId); // also clears any acting-user
    handleClose();
    window.location.reload();
  };
  const commitUser = (orgId, user) => {
    setActingOrg(orgId);     // set org first (this clears acting-user)…
    setActingUser(user);     // …then pin the specific user within it
    handleClose();
    window.location.reload();
  };

  return (
    <Box sx={{ mt: 2, mb: 1 }}>
      <Button
        onClick={handleOpen}
        size="small"
        startIcon={!collapsed && <SwapHorizIcon fontSize="small" />}
        sx={{
          width: "100%",
          justifyContent: collapsed ? "center" : "flex-start",
          textTransform: "none",
          color: actingOrgId ? "warning.dark" : "text.secondary",
          bgcolor: actingOrgId ? "warning.light" : "transparent",
          borderRadius: 1.5,
          px: collapsed ? 0.5 : 1.5,
          py: 0.75,
          minWidth: 0,
          "&:hover": { bgcolor: actingOrgId ? "warning.light" : theme.palette.action.hover },
        }}
      >
        {collapsed ? (
          <SwapHorizIcon fontSize="small" />
        ) : (
          <Typography variant="caption" noWrap sx={{ fontWeight: 600 }}>
            {label}
          </Typography>
        )}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{ sx: { maxHeight: 460, minWidth: 268 } }}
      >
        {FEATURE_VIEW_AS_USER && (
          <Typography
            variant="caption"
            sx={{ px: 2, pt: 1, pb: 0.5, display: "block", fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase", color: "text.secondary" }}
          >
            Choose an org, then a user
          </Typography>
        )}
        <MenuItem onClick={actAsSelf} sx={{ fontWeight: actingOrgId ? 400 : 700 }}>
          {!actingOrgId && <CheckIcon fontSize="small" sx={{ mr: 1 }} />}
          <Typography variant="body2">Act as myself</Typography>
        </MenuItem>
        <Divider />

        {orgs.map((o) => {
          const isActingOrg = o.org_id === actingOrgId;
          const expanded = FEATURE_VIEW_AS_USER && expandedOrgId === o.org_id;
          return (
            <Box key={o.org_id}>
              {/* Org row. With the flag on, clicking expands the user list
                  inline (browse only). With the flag off, it commits org-level
                  impersonation directly (no user step). */}
              <MenuItem
                onClick={() => (FEATURE_VIEW_AS_USER ? toggleExpand(o.org_id) : commitOrgWide(o.org_id))}
                sx={{ fontWeight: isActingOrg ? 700 : 400 }}
              >
                {FEATURE_VIEW_AS_USER
                  ? (expanded
                      ? <KeyboardArrowDownIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />
                      : <KeyboardArrowRightIcon fontSize="small" sx={{ mr: 1, opacity: 0.7 }} />)
                  : (isActingOrg && <CheckIcon fontSize="small" sx={{ mr: 1 }} />)}
                <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>{o.domain}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>{o.org_id}</Typography>
                </Box>
                {/* Green tick when this org is committed (org-wide, no user pinned) */}
                {FEATURE_VIEW_AS_USER && isActingOrg && !actingUserId && (
                  <CheckIcon fontSize="small" sx={{ ml: 1, color: "success.main" }} />
                )}
              </MenuItem>

              {/* Inline user list for the expanded org */}
              {expanded && (
                <Box sx={{ bgcolor: theme.palette.action.hover }}>
                  {loadingUsers ? (
                    <MenuItem disabled sx={{ pl: 5 }}>
                      <Typography variant="caption" color="text.secondary">Loading users…</Typography>
                    </MenuItem>
                  ) : (
                    <>
                      <MenuItem
                        onClick={() => commitOrgWide(o.org_id)}
                        sx={{ pl: 5, fontWeight: isActingOrg && !actingUserId ? 700 : 400 }}
                      >
                        {isActingOrg && !actingUserId
                          ? <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                          : <Box sx={{ width: 20, mr: 1 }} />}
                        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" noWrap>Org Admin</Typography>
                          <Typography variant="caption" color="warning.main" noWrap>org-wide · read-write</Typography>
                        </Box>
                      </MenuItem>
                      {expandedUsers.map((u) => {
                        const key = userKey(u);
                        const isTest = !!(u.is_test || u.is_test_user);
                        const selected = isActingOrg && key === actingUserId;
                        return (
                          <MenuItem
                            key={key}
                            onClick={() => commitUser(o.org_id, u)}
                            sx={{ pl: 5, fontWeight: selected ? 700 : 400 }}
                          >
                            {selected
                              ? <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                              : <PersonIcon fontSize="small" sx={{ mr: 1, opacity: 0.5 }} />}
                            <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" noWrap>{u.email || u.display_name || key}</Typography>
                              <Typography variant="caption" color={isTest ? "success.main" : "text.secondary"} noWrap>
                                {isTest ? "test account · read-write" : "read-only"}
                              </Typography>
                            </Box>
                          </MenuItem>
                        );
                      })}
                      {expandedUsers.length === 0 && (
                        <MenuItem disabled sx={{ pl: 5 }}>
                          <Typography variant="caption" color="text.secondary">No users found for this org</Typography>
                        </MenuItem>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Box>
          );
        })}
        {orgs.length === 0 && (
          <MenuItem disabled>
            <Typography variant="caption" color="text.secondary">No orgs found</Typography>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
